/* yl-infinite-scroll.js — scroll-loading pagination for collection + search grids.
 *
 * User decision (spec 2026-11-01 #6): "商品列表分页用滚动加载分页，不用传统分页" — keep a
 * normal aligned grid, but load the next page automatically while scrolling.
 *
 * Markup contract (sections/collection.liquid, sections/search.liquid):
 *   [data-yl-infinite]            results wrapper; data-section-id/page/pages/total,
 *                                 data-status-template ("__SHOWN__"/"__TOTAL__"),
 *                                 data-done-template ("__TOTAL__")
 *     [data-yl-infinite-prev-wrap] > [data-yl-infinite-prev]   (only when page > 1)
 *     [data-yl-infinite-grid]      the product grid; its children are the cards
 *     [data-yl-infinite-footer]    status / loader / error / [data-yl-infinite-more]
 *     .yl-pagination               classic links — kept for no-JS + crawlers, hidden here
 *
 * Next pages come from the Section Rendering API (`?sections=<section.id>` returns
 * JSON {id: html}; shopify.dev/docs/api/ajax/section-rendering) on the same URL with
 * page=N, so filters and sort are carried by the current query string. If that
 * response is unusable we fall back to the full document once, then show a retry
 * button. The URL keeps ?page=N in sync (history.replaceState) and the scroll offset
 * inside that page is stored in history.state so a non-bfcache back navigation
 * lands near the same product.
 *
 * Appended cards rely on document-level delegation elsewhere: add-to-cart submit
 * (yololife-cart-drawer.js) and the card gallery (yl-card-gallery.js). After each
 * insert a `yl:products-appended` event is dispatched on document for anything else.
 */
(function () {
  'use strict';

  if (!window.fetch || !window.DOMParser || !window.URL) return;

  const PREFETCH_MARGIN = '0px 0px 600px 0px';
  const URL_LINE = 0.35;        // a page "owns" the URL once its first card passes 35% of the viewport
  const SAVE_DELAY = 400;       // debounce for history.state writes (Safari caps replaceState rate)

  const toInt = (value, fallback) => {
    const number = parseInt(value, 10);
    return Number.isFinite(number) ? number : fallback;
  };

  const fill = (template, values) => Object.keys(values).reduce(
    (text, token) => text.split(token).join(String(values[token])),
    template || ''
  );

  const init = (root) => {
    const grid = root.querySelector('[data-yl-infinite-grid]');
    const footer = root.querySelector('[data-yl-infinite-footer]');
    const pages = toInt(root.dataset.pages, 1);
    const total = toInt(root.dataset.total, 0);
    const startPage = toInt(root.dataset.page, 1);
    if (!grid || !footer || pages <= 1) return;

    const sectionId = root.dataset.sectionId || '';
    const status = footer.querySelector('[data-yl-infinite-status]');
    const loader = footer.querySelector('[data-yl-infinite-loader]');
    const errorNote = footer.querySelector('[data-yl-infinite-error]');
    const moreButton = footer.querySelector('[data-yl-infinite-more]');
    const prevWrap = root.querySelector('[data-yl-infinite-prev-wrap]');
    const prevButton = root.querySelector('[data-yl-infinite-prev]');
    const autoLoad = 'IntersectionObserver' in window;

    const base = new URL(window.location.href);
    base.hash = '';
    ['page', 'sections', 'section_id'].forEach((key) => base.searchParams.delete(key));
    const pageUrl = (page) => {
      const url = new URL(base.href);
      if (page > 1) url.searchParams.set('page', String(page));
      return url;
    };

    let minPage = startPage;
    let maxPage = startPage;
    let loading = false;
    let failed = false;
    let observer = null;

    // Duplicate guard: Shopify can return an overlapping page if inventory changes mid-session.
    const seen = new Set();
    // Key = the product URL (a card may contain other links, e.g. enquiry/in-store, before it).
    const keyOf = (item) => {
      if (!item.querySelector) return '';
      const link = item.matches('a[href]')
        ? item
        : item.querySelector('a[href*="/products/"]') || item.querySelector('a[href]');
      const href = link && link.getAttribute('href');
      if (!href) return '';
      try {
        return new URL(href, window.location.href).pathname;
      } catch (_) {
        return '';
      }
    };
    Array.from(grid.children).forEach((item) => {
      const key = keyOf(item);
      if (key) seen.add(key);
    });
    if (grid.firstElementChild) grid.firstElementChild.dataset.ylPage = String(startPage);

    root.classList.add('is-yl-infinite');

    const updateStatus = () => {
      if (!status) return;
      const complete = maxPage >= pages && minPage <= 1;
      status.textContent = complete && root.dataset.doneTemplate
        ? fill(root.dataset.doneTemplate, { __TOTAL__: total })
        : fill(root.dataset.statusTemplate, { __SHOWN__: grid.children.length, __TOTAL__: total });
      root.classList.toggle('is-complete', maxPage >= pages);
    };

    const setBusy = (busy) => {
      root.classList.toggle('is-fetching', busy);
      grid.setAttribute('aria-busy', busy ? 'true' : 'false');
      if (loader) loader.hidden = !busy;
    };

    const refreshControls = () => {
      const hasNext = maxPage < pages;
      if (errorNote) errorNote.hidden = !failed;
      if (moreButton) {
        moreButton.hidden = !(hasNext && (failed || !autoLoad));
        if (hasNext) moreButton.href = pageUrl(maxPage + 1).href;
      }
      if (prevWrap) prevWrap.hidden = minPage <= 1;
      if (prevButton && minPage > 1) prevButton.href = pageUrl(minPage - 1).href;
      if (!observer) return;
      if (!hasNext) {
        observer.disconnect();
        observer = null;
      } else if (!failed) {
        // Re-observe so a sentinel that is still on screen (tall viewport) fires again.
        observer.unobserve(footer);
        observer.observe(footer);
      }
    };

    const parse = (html) => {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fetchedRoot = doc.querySelector('[data-yl-infinite]');
      const fetchedGrid = fetchedRoot && fetchedRoot.querySelector('[data-yl-infinite-grid]');
      if (!fetchedGrid) return null;
      return { page: toInt(fetchedRoot.dataset.page, NaN), items: Array.from(fetchedGrid.children) };
    };

    const fetchPage = async (page) => {
      const url = pageUrl(page);
      if (sectionId) {
        try {
          const sectionUrl = new URL(url.href);
          sectionUrl.searchParams.set('sections', sectionId);
          // No Accept: application/json header — with it Shopify answers with the collection's own JSON
          // resource instead of the sections payload (实测 theme dev 2026-11-01).
          const response = await fetch(sectionUrl.href, { credentials: 'same-origin' });
          if (response.ok) {
            const data = await response.json();
            const parsed = data && typeof data[sectionId] === 'string' ? parse(data[sectionId]) : null;
            if (parsed) return parsed;
          }
          console.warn('[yl-infinite] section render unusable, falling back to full page', response.status);
        } catch (error) {
          console.warn('[yl-infinite] section render failed, falling back to full page', error);
        }
      }
      const response = await fetch(url.href, { credentials: 'same-origin' });
      if (!response.ok) throw new Error('HTTP ' + response.status);
      const parsed = parse(await response.text());
      if (!parsed) throw new Error('product grid missing in page ' + page);
      return parsed;
    };

    const insert = (items, page, prepend) => {
      const fragment = document.createDocumentFragment();
      const added = [];
      items.forEach((item) => {
        const key = keyOf(item);
        if (key && seen.has(key)) return;
        if (key) seen.add(key);
        const node = document.importNode(item, true);
        if (!added.length) node.dataset.ylPage = String(page);
        added.push(node);
        fragment.appendChild(node);
      });
      if (prepend) grid.insertBefore(fragment, grid.firstChild);
      else grid.appendChild(fragment);
      if (added.length) {
        document.dispatchEvent(new CustomEvent('yl:products-appended', {
          detail: { container: grid, items: added, page, position: prepend ? 'start' : 'end' },
        }));
      }
      return added;
    };

    const focusFirstLink = (item, preventScroll) => {
      const link = item && (item.matches('a[href]') ? item : item.querySelector('a[href]'));
      if (link) link.focus({ preventScroll });
    };

    const loadNext = async (manual) => {
      if (loading || maxPage >= pages) return;
      loading = true;
      failed = false;
      if (errorNote) errorNote.hidden = true;
      setBusy(true);
      const page = maxPage + 1;
      try {
        const result = await fetchPage(page);
        if (result.page !== page || !result.items.length) {
          // Shopify clamps out-of-range pages; treat as the end rather than repeating a page.
          maxPage = pages;
        } else {
          const added = insert(result.items, page, false);
          maxPage = page;
          // "Load more" pressed by hand: move focus (and the view) to the first new product.
          if (manual && added.length) focusFirstLink(added[0], false);
        }
        updateStatus();
      } catch (error) {
        failed = true;
        console.warn('[yl-infinite] could not load page ' + page, error);
      } finally {
        loading = false;
        setBusy(false);
        refreshControls();
      }
    };

    const loadPrev = async () => {
      if (loading || minPage <= 1) return;
      loading = true;
      setBusy(true);
      const page = minPage - 1;
      const anchor = prevWrap || grid;
      const anchorTop = anchor.getBoundingClientRect().top;
      try {
        const result = await fetchPage(page);
        if (result.page !== page || !result.items.length) {
          minPage = 1;
        } else {
          const added = insert(result.items, page, true);
          minPage = page;
          if (minPage <= 1 && prevWrap) prevWrap.hidden = true;
          // New cards land directly under the control the shopper pressed; undo any
          // browser scroll anchoring so the viewport does not jump past them.
          const target = minPage > 1 && prevWrap ? prevWrap : (added[0] || grid);
          window.scrollBy(0, target.getBoundingClientRect().top - anchorTop);
          if (minPage <= 1 && added.length) focusFirstLink(added[0], true);
        }
        updateStatus();
      } catch (error) {
        console.warn('[yl-infinite] could not load previous page ' + page, error);
      } finally {
        loading = false;
        setBusy(false);
        refreshControls();
        saveState();
      }
    };

    // --- URL + scroll position ---------------------------------------------
    let reportedPage = startPage;
    let saveTimer = 0;

    const visiblePage = () => {
      const line = window.innerHeight * URL_LINE;
      let page = minPage;
      grid.querySelectorAll('[data-yl-page]').forEach((marker) => {
        if (marker.getBoundingClientRect().top <= line) page = toInt(marker.dataset.ylPage, page);
      });
      return page;
    };

    function saveState() {
      window.clearTimeout(saveTimer);
      const page = visiblePage();
      const marker = grid.querySelector('[data-yl-page="' + page + '"]');
      const offset = marker ? Math.round(-marker.getBoundingClientRect().top) : 0;
      const url = pageUrl(page);
      url.hash = window.location.hash;
      const previous = history.state && typeof history.state === 'object' ? history.state : {};
      try {
        history.replaceState(Object.assign({}, previous, { ylInfinite: { page, offset } }), '', url.href);
        reportedPage = page;
      } catch (_) {
        /* replaceState can throw when rate-limited; the next save retries. */
      }
    }

    let scrollQueued = false;
    window.addEventListener('scroll', () => {
      if (scrollQueued) return;
      scrollQueued = true;
      window.requestAnimationFrame(() => {
        scrollQueued = false;
        if (visiblePage() !== reportedPage) saveState();
        window.clearTimeout(saveTimer);
        saveTimer = window.setTimeout(saveState, SAVE_DELAY);
      });
    }, { passive: true });
    window.addEventListener('pagehide', saveState);

    // Restore the in-page offset after a back navigation that rebuilt the document.
    if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
    const saved = history.state && history.state.ylInfinite;
    if (saved && saved.page === startPage && Number.isFinite(saved.offset)) {
      window.requestAnimationFrame(() => {
        const marker = grid.firstElementChild;
        if (marker) window.scrollTo(0, marker.getBoundingClientRect().top + window.scrollY + saved.offset);
      });
    }

    // --- Controls ------------------------------------------------------------
    if (moreButton) {
      moreButton.addEventListener('click', (event) => {
        event.preventDefault();
        loadNext(true);
      });
    }
    if (prevButton) {
      prevButton.addEventListener('click', (event) => {
        event.preventDefault();
        loadPrev();
      });
    }

    if (autoLoad && maxPage < pages) {
      observer = new IntersectionObserver((entries) => {
        if (failed || loading) return;
        if (entries.some((entry) => entry.isIntersecting)) loadNext(false);
      }, { rootMargin: PREFETCH_MARGIN });
      observer.observe(footer);
    }

    updateStatus();
    refreshControls();
  };

  document.querySelectorAll('[data-yl-infinite]').forEach(init);
})();
